import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Send, MessageSquare, Loader2, User, Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [newConvTo, setNewConvTo] = useState("");
  const [newConvMsg, setNewConvMsg] = useState("");
  const [sending, setSending] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`);
      setConversations(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const loadMessages = async (convId) => {
    setSelectedConv(convId);
    try {
      const res = await axios.get(`${API}/messages/${convId}`);
      setMessages(res.data);
    } catch (err) { console.error(err); }
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !selectedConv) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/${selectedConv}/send`, { content: newMsg });
      setNewMsg("");
      loadMessages(selectedConv);
    } catch (err) { toast.error("Blad wysylki"); }
    finally { setSending(false); }
  };

  const startConversation = async () => {
    if (!newConvTo.trim() || !newConvMsg.trim()) return;
    try {
      const res = await axios.post(`${API}/messages/new`, { to_identifier: newConvTo, content: newConvMsg });
      setNewConvTo(""); setNewConvMsg("");
      fetchConversations();
      loadMessages(res.data.conversation_id);
      toast.success("Wiadomosc wyslana");
    } catch (err) { toast.error(err.response?.data?.detail || "Blad"); }
  };

  return (
    <div data-testid="messages-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Wiadomosci</h1>
        <p className="text-sm text-muted-foreground mt-1">Komunikacja z wydawcami, reklamodawcami i adminem</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{height: "calc(100vh - 250px)"}}>
        {/* Lista konwersacji */}
        <div className="space-y-3">
          {/* Nowa konwersacja */}
          <Card className="rounded-xl border bg-card">
            <CardContent className="p-3 space-y-2">
              <p className="text-xs font-semibold">Nowa wiadomosc</p>
              <Input value={newConvTo} onChange={(e) => setNewConvTo(e.target.value)}
                placeholder="Email lub nick odbiorcy" className="h-8 text-xs" />
              <Textarea value={newConvMsg} onChange={(e) => setNewConvMsg(e.target.value)}
                placeholder="Tresc wiadomosci..." rows={2} className="resize-none text-xs" />
              <Button size="sm" className="w-full gap-1" onClick={startConversation}>
                <Send className="w-3 h-3" /> Wyslij
              </Button>
            </CardContent>
          </Card>

          <Card className="rounded-xl border bg-card">
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                {conversations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-20" />
                    <p className="text-xs">Brak konwersacji</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {conversations.map((conv) => (
                      <button key={conv.id} onClick={() => loadMessages(conv.id)}
                        className={`w-full text-left p-3 hover:bg-accent/30 transition-colors ${selectedConv === conv.id ? "bg-primary/5" : ""}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-foreground truncate">{conv.other_user}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{conv.last_message}</p>
                          </div>
                          {conv.unread > 0 && <Badge className="bg-blue-600 text-white text-[9px] rounded-full">{conv.unread}</Badge>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Wiadomosci */}
        <div className="lg:col-span-2">
          <Card className="rounded-xl border bg-card h-full flex flex-col">
            {!selectedConv ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">Wybierz konwersacje lub rozpocznij nowa</p>
                </div>
              </div>
            ) : (
              <>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.map((msg) => {
                      const isMine = msg.sender_id === user?.id;
                      return (
                        <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[70%] p-3 rounded-2xl ${
                            isMine ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-100 dark:bg-slate-800 rounded-bl-sm"
                          }`}>
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-[9px] mt-1 ${isMine ? "text-blue-200" : "text-muted-foreground"}`}>
                              {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
                <div className="p-3 border-t flex gap-2">
                  <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Napisz wiadomosc..." className="flex-1" />
                  <Button onClick={sendMessage} disabled={sending} className="gap-1">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
